/**
 * Попап креатива в «Пульсе»: превью и графики по дням — 7 дней нормы и
 * выбранный день (он выделен). Сутки календарные, как их отдаёт Meta.
 * Данные приходят вместе с пульсом (detail.meta.creativeDays), отдельно
 * ничего не грузим.
 */
import React, { useEffect } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

export interface CreativeDay {
  date: string;
  leads: number;
  spend: number;
  clicks: number;
  impressions: number;
}

const BAR = "#2a78d6";
const BAR_DIM = "rgba(42, 120, 214, 0.35)";

const ddmm = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;
const num = (v: number | null, digits = 0) =>
  v === null ? "—" : v.toLocaleString("ru-RU", { maximumFractionDigits: digits, minimumFractionDigits: digits });

const MiniChart: React.FC<{
  title: string;
  days: CreativeDay[];
  values: (number | null)[];
  digits?: number;
  suffix?: string;
}> = ({ title, days, values, digits = 0, suffix = "" }) => {
  const last = values.length - 1;
  const known = values.filter((v): v is number => v !== null);
  const avg = known.length > 1 ? known.slice(0, -1).reduce((a, b) => a + b, 0) / (known.length - 1) : null;
  return (
    <div className="pulse-cm__chart">
      <div className="pulse-cm__chart-head">
        <span>{title}</span>
        <span>
          <b>
            {num(values[last], digits)}
            {values[last] === null ? "" : suffix}
          </b>
          {avg !== null && (
            <span className="pulse-muted">
              {" "}
              · в среднем {num(avg, digits)}
              {suffix}
            </span>
          )}
        </span>
      </div>
      <div className="pulse-cm__canvas">
        <Bar
          data={{
            labels: days.map((d) => ddmm(d.date)),
            datasets: [
              {
                data: values,
                backgroundColor: values.map((_, i) => (i === last ? BAR : BAR_DIM)),
                borderRadius: 3,
                maxBarThickness: 28,
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            plugins: {
              legend: { display: false },
              tooltip: { callbacks: { label: (c) => `${num(c.parsed.y, digits)}${suffix}` } },
            },
            scales: {
              x: { grid: { display: false }, ticks: { color: "#999", font: { size: 10 } } },
              y: {
                beginAtZero: true,
                grid: { color: "#f0f0f0" },
                ticks: { color: "#999", font: { size: 10 }, maxTicksLimit: 4 },
              },
            },
          }}
        />
      </div>
    </div>
  );
};

const CreativeModal: React.FC<{
  label: string;
  campaign: string;
  thumb?: string;
  previewLink?: string;
  isVideo?: boolean;
  currency: string;
  isToday: boolean;
  days: CreativeDay[];
  onClose: () => void;
}> = ({ label, campaign, thumb, previewLink, isVideo, currency, isToday, days, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const total = days.reduce(
    (a, d) => ({ leads: a.leads + d.leads, spend: a.spend + d.spend, clicks: a.clicks + d.clicks }),
    { leads: 0, spend: 0, clicks: 0 }
  );

  return (
    <div className="pulse-cm__overlay" onClick={onClose}>
      <div className="pulse-cm" onClick={(e) => e.stopPropagation()}>
        <div className="pulse-cm__head">
          <div>
            <div className="pulse-cm__title">{label}</div>
            <div className="pulse-muted pulse-small">{campaign}</div>
          </div>
          <button className="pulse-cm__close" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <div className="pulse-cm__body">
          <div className="pulse-cm__media">
            {thumb ? (
              <img src={thumb} alt={label} />
            ) : (
              <div className="pulse-cm__noimg pulse-muted">нет превью</div>
            )}
            {isVideo && <span className="pulse-tag">видео</span>}
            {previewLink && (
              <a href={previewLink} target="_blank" rel="noreferrer">
                Открыть объявление
              </a>
            )}
            <div className="pulse-cm__totals">
              <div>
                За {days.length} дн.: <b>{num(total.leads)}</b> лидов
              </div>
              <div>
                Спенд <b>{num(total.spend, 2)}</b> {currency}
              </div>
              <div>
                Цена лида <b>{total.leads ? num(total.spend / total.leads, 2) : "—"}</b>
              </div>
            </div>
          </div>
          <div className="pulse-cm__charts">
            <MiniChart title="Лиды" days={days} values={days.map((d) => d.leads)} />
            <MiniChart title={`Спенд, ${currency}`} days={days} values={days.map((d) => d.spend)} digits={2} />
            <MiniChart
              title={`Цена лида, ${currency}`}
              days={days}
              values={days.map((d) => (d.leads ? d.spend / d.leads : null))}
              digits={2}
            />
            <MiniChart
              title="CTR"
              days={days}
              values={days.map((d) => (d.impressions ? (d.clicks / d.impressions) * 100 : null))}
              digits={2}
              suffix="%"
            />
          </div>
        </div>
        <div className="pulse-cm__foot pulse-muted pulse-small">
          Календарные сутки по Алматы. Выделен выбранный день{isToday ? " — он ещё идёт, цифры неполные" : ""}; «в среднем» — по
          предыдущим дням.
        </div>
      </div>
    </div>
  );
};

export default CreativeModal;
